/**
 * WidgetCard — Container for a single analytics widget. Renders the widget
 * title, chart visualization (via ChartRenderer), metadata, and action controls
 * (export CSV, delete). Clicking chart segments triggers the drill-down flow.
 *
 * Requirements: 10.1, 12.1
 */

import React, { useCallback, useState } from 'react';
import type { AnalyticsWidget } from '@coreflow/shared';
import { ChartRenderer } from './ChartRenderer';
import * as analyticsApi from '../../services/analytics-api';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface WidgetCardProps {
  readonly widget: AnalyticsWidget;
  readonly data: Array<{ dimensionValue: string; measureValue: number }> | null;
  readonly hasAccess: boolean;
  readonly isOwner: boolean;
  readonly dashboardId: string;
  readonly onDrillDown?: (dimensionValue: string) => void;
  readonly onDelete?: () => void;
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/** Formats a Dimension enum value into a readable label. */
function formatDimensionLabel(dimension: string): string {
  return dimension
    .split('-')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
}

/** Formats a Measure object into a readable label. */
function formatMeasureLabel(measure: AnalyticsWidget['measure']): string {
  switch (measure.type) {
    case 'card-count':
      return 'Card Count';
    case 'numeric-field-sum':
      return 'Sum';
    case 'numeric-field-avg':
      return 'Average';
    default:
      return 'Value';
  }
}

/** Formats a chart type value into a readable label. */
function formatChartType(chartType: string): string {
  return chartType
    .split('-')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function WidgetCard({
  widget,
  data,
  hasAccess,
  isOwner,
  dashboardId,
  onDrillDown,
  onDelete,
}: WidgetCardProps): React.ReactElement {
  const [isExporting, setIsExporting] = useState(false);

  const handleSegmentClick = useCallback(
    (dimensionValue: string) => {
      onDrillDown?.(dimensionValue);
    },
    [onDrillDown],
  );

  const handleExportCsv = useCallback(async () => {
    if (isExporting) return;
    setIsExporting(true);

    try {
      const csvData = await analyticsApi.exportCsv(dashboardId, widget.widgetId);
      const blob = csvData instanceof Blob
        ? csvData
        : new Blob([csvData as unknown as string], { type: 'text/csv;charset=utf-8' });
      const url = URL.createObjectURL(blob);
      const anchor = document.createElement('a');
      anchor.href = url;
      anchor.download = `${widget.title.replace(/[^a-zA-Z0-9_-]/g, '_')}.csv`;
      anchor.click();
      URL.revokeObjectURL(url);
    } catch {
      // Export failed — silent for now; could surface via toast in the future
    } finally {
      setIsExporting(false);
    }
  }, [dashboardId, widget.widgetId, widget.title, isExporting]);

  const handleDelete = useCallback(() => {
    onDelete?.();
  }, [onDelete]);

  const dimensionLabel = formatDimensionLabel(widget.dimension);
  const measureLabel = formatMeasureLabel(widget.measure);
  const boardCount = widget.dataSourceBoardIds.length;

  return (
    <article
      className="flex flex-col rounded-lg border border-border bg-card shadow-sm"
      aria-label={`Widget: ${widget.title}`}
    >
      {/* Header */}
      <header className="flex items-center justify-between border-b border-border px-4 py-3">
        <h3 className="text-sm font-semibold text-foreground truncate" title={widget.title}>
          {widget.title}
        </h3>
        <div className="flex items-center gap-1 shrink-0 ml-2">
          {/* Export CSV */}
          <button
            type="button"
            onClick={handleExportCsv}
            disabled={isExporting || !hasAccess}
            className="rounded p-1.5 text-muted-foreground hover:text-foreground hover:bg-accent disabled:opacity-50 disabled:cursor-not-allowed focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            aria-label={`Export CSV for ${widget.title}`}
            title="Export CSV"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              aria-hidden="true"
            >
              <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4" />
              <polyline points="7 10 12 15 17 10" />
              <line x1="12" y1="15" x2="12" y2="3" />
            </svg>
          </button>

          {/* Delete (owner only) */}
          {isOwner && onDelete && (
            <button
              type="button"
              onClick={handleDelete}
              className="rounded p-1.5 text-muted-foreground hover:text-destructive hover:bg-destructive/10 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              aria-label={`Delete widget ${widget.title}`}
              title="Delete widget"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                aria-hidden="true"
              >
                <polyline points="3 6 5 6 21 6" />
                <path d="M19 6l-1 14a2 2 0 01-2 2H8a2 2 0 01-2-2L5 6" />
                <path d="M10 11v6" />
                <path d="M14 11v6" />
                <path d="M9 6V4a1 1 0 011-1h4a1 1 0 011 1v2" />
              </svg>
            </button>
          )}
        </div>
      </header>

      {/* Chart area */}
      <div className="flex-1 p-4">
        <ChartRenderer
          chartType={widget.chartType}
          data={data}
          dimensionLabel={dimensionLabel}
          measureLabel={measureLabel}
          hasAccess={hasAccess}
          onSegmentClick={handleSegmentClick}
        />
      </div>

      {/* Footer metadata */}
      <footer className="flex items-center justify-between border-t border-border px-4 py-2 text-xs text-muted-foreground">
        <span>
          {formatChartType(widget.chartType)} · {boardCount} {boardCount === 1 ? 'source' : 'sources'}
        </span>
        <span>{dimensionLabel}</span>
      </footer>
    </article>
  );
}
